import { useMemo } from "react";

import useLabel from "@apps/use-label/use-label";

import IntegrationCard from "./integrations-card";
import { getNativeIntegrationsConfig } from "./integrations-config";
import { NativeIntegration } from "./integrations-types";

const IntegrationsMarketNative = ({
  organization,
  connectedNativeIntegrations,
  onSlackConnect,
  onRefetch: _onRefetch,
}: {
  organization: any;
  connectedNativeIntegrations: NativeIntegration[];
  onSlackConnect?: () => void;
  onRefetch: () => void;
}) => {
  const label = useLabel();

  // ---------------------------------------------------------------------------
  // Build available (not connected) native integrations
  // ---------------------------------------------------------------------------
  const availableNativeIntegrations = useMemo(() => {
    const nativeIntegationsConfig = getNativeIntegrationsConfig(
      organization,
      label,
    );
    const connected = new Set(connectedNativeIntegrations.map((i) => i.slug));
    const allNative = [
      {
        slug: "slack",
        ...nativeIntegationsConfig.slack,
        connectUrl: `/sso/slack?next=/settings/organization/${organization.id}/integrations&org=${organization.id}`,
      },
      {
        slug: "salesforce",
        ...nativeIntegationsConfig.salesforce,
        connectUrl: `/sso/salesforce?next=/settings/organization/${organization.id}/integrations&integration_link_to_org_id=${organization.id}`,
      },
      {
        slug: "teams",
        ...nativeIntegationsConfig.teams,
        connectUrl: `/sso/microsoft?request_transcript_scopes=true&request_calendar_scope=true&request_bot_scope=true&next=/msteams/subscribe_to_transcripts?org_id=${organization.id}&integration_link_to_org_id=${organization.id}`,
      },
      {
        slug: "github",
        ...nativeIntegationsConfig.github,
        connectUrl: import.meta.env.DEV
          ? `https://github.com/apps/topicflow-dev/installations/select_target`
          : `https://github.com/apps/topicflow/installations/select_target`,
      },
      {
        slug: "linear",
        ...nativeIntegationsConfig.linear,
        connectUrl: `/sso/linear?next=/settings/organization/${organization.id}/integrations&integration_link_to_org_id=${organization.id}`,
      },
      {
        slug: "youtrack",
        ...nativeIntegationsConfig.youtrack,
        connectUrl: `/sso/youtrack?next=/settings/organization/${organization.id}/integrations&integration_link_to_org_id=${organization.id}`,
      },
    ];
    return allNative.filter((i) => !connected.has(i.slug));
  }, [connectedNativeIntegrations, organization, label]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      {availableNativeIntegrations.map((integration) => (
        <IntegrationCard
          key={integration.slug}
          name={integration.name}
          logo={integration.logo}
          description={integration.description}
          onConnect={() => {
            if (integration.slug === "slack" && onSlackConnect) {
              onSlackConnect();
              return;
            }
            window.location.href = integration.connectUrl;
          }}
        />
      ))}
    </>
  );
};

export { IntegrationsMarketNative };
